export function ageFrom(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Date.now() - dt.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

export function groupSquad(players) {
  const buckets = { GK: [], DEF: [], MID: [], ATT: [] };
  (Array.isArray(players) ? players : []).forEach((pl) => {
    const pos = (pl?.position?.name || pl?.position || '').toUpperCase();
    let key = 'MID';
    if (pos.includes('GK') || pos.includes('GOAL')) key = 'GK';
    else if (pos.includes('DEF') || pos.includes('BACK') || /^D/.test(pos)) key = 'DEF';
    else if (pos.includes('ATT') || pos.includes('FW') || pos.includes('ST') || pos.includes('FORWARD')) key = 'ATT';
    else if (pos.includes('MID')) key = 'MID';
    buckets[key].push(pl);
  });
  return ['GK', 'DEF', 'MID', 'ATT'].filter((g) => buckets[g].length).map((g) => ({ group: g, players: buckets[g] }));
}

export function playerMap(players) {
  const m = {};
  (Array.isArray(players) ? players : []).forEach((pl) => {
    m[pl?.id || pl?.player_id] = pl?.display_name || pl?.name || [pl?.firstname, pl?.lastname].filter(Boolean).join(' ') || '—';
  });
  return m;
}

export function statVal(details, code) {
  if (!Array.isArray(details)) return null;
  const d = details.find((x) => x?.type?.code === code || x?.code === code);
  if (!d) return null;
  const v = d.value ?? d;
  if (v == null) return null;
  if (typeof v !== 'object') return v;
  if ('average' in v) return v.average;
  if ('total' in v) return v.total;
  if ('count' in v) return v.count;
  if ('value' in v) return v.value;
  return null;
}

export function getActiveStats(overview) {
  const seasons = overview?.active_seasons || overview?.activeseasons || [];
  return Array.isArray(seasons) ? seasons[0] : seasons || null;
}

export function resultFor(fixture, clubName) {
  const t = (fixture?.result_info || fixture?.info || '').toLowerCase();
  const home = (fixture?.home?.name || '').toLowerCase();
  const away = (fixture?.away?.name || '').toLowerCase();
  const me = (clubName || '').toLowerCase();
  const isHome = home === me;
  const won = t.includes('won');
  const draw = t.includes('draw');
  if (draw) return 'D';
  if (won) {
    if (isHome && t.includes('home won')) return 'W';
    if (!isHome && t.includes('away won')) return 'W';
    if (t.includes(`${home} won`)) return isHome ? 'W' : 'L';
    if (t.includes(`${away} won`)) return isHome ? 'L' : 'W';
    return t.includes(`${home} won`) ? (isHome ? 'W' : 'L') : (isHome ? 'L' : 'W');
  }
  return null;
}

export function displayStatValue(v) {
  if (v == null) return '—';
  if (typeof v !== 'object') return v;
  if ('average' in v) return v.average;
  if ('total' in v) return v.total;
  if ('count' in v) return v.count;
  return '—';
}

export const FORM_BADGE = { W: 'i-bw', D: 'i-bd', L: 'i-bl' };

export function fmtMonthYear(d) {
  if (!d) return '—';
  const dt = new Date(typeof d === 'string' ? d.replace(' ', 'T') : d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export function activeTeamSeason(overview) {
  const seasons = overview?.activeseasons || overview?.active_seasons || [];
  return Array.isArray(seasons)
    ? (seasons.find((s) => s?.is_current) || seasons[0] || null)
    : (seasons || null);
}

export function teamStatistics(overview) {
  return Array.isArray(overview?.statistics) ? overview.statistics : [];
}

export function statsForSeason(overview, seasonId) {
  const all = teamStatistics(overview);
  if (!all.length) return null;
  let sid = seasonId;
  if (sid == null) {
    const active = activeTeamSeason(overview);
    sid = active?.id;
  }
  const m = (sid != null) ? all.find((s) => s?.season_id === sid || s?.season?.id === sid) : null;
  return m || all[0] || null;
}

export function standingsDetailValue(row, code) {
  const d = Array.isArray(row?.details) ? row.details.find((x) => x?.type?.code === code) : null;
  return d?.value ?? null;
}

export function latestFixtures(overview, recentFixtures) {
  if (Array.isArray(recentFixtures) && recentFixtures.length) return recentFixtures;
  return Array.isArray(overview?.latest) ? overview.latest : [];
}

export function latestResult(f) {
  const t = (f?.result_info || '').toLowerCase();
  if (!t) return null;
  if (t.includes('draw')) return 'D';
  const isHome = (f?.meta?.location || '').toLowerCase() === 'home';
  const parts = String(f?.name || '').split(/\s+vs\s+/i);
  const our = (isHome ? parts[0] : parts[1] || '').trim().toLowerCase();
  const won = !!our && t.startsWith(our) && t.includes('won');
  return won ? 'W' : 'L';
}

export function fmtMDY(d) {
  if (!d) return '—';
  const dt = new Date(typeof d === 'string' ? d.replace(' ', 'T') : d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function positionFamily(pos) {
  const p = String(pos || '').toLowerCase();
  if (p.includes('keeper') || p === 'gk') return 'GK';
  if (p.includes('defend') || p.includes('back')) return 'DEF';
  if (p.includes('attack') || p.includes('forward') || p.includes('striker') || p.includes('winger')) return 'ATT';
  if (p.includes('mid')) return 'MID';
  return 'MID';
}

export function titleize(str) {
  if (!str) return '';
  return String(str).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function buildSquadNameMap(data) {
  const map = {};
  const add = (p) => {
    if (!p?.id) return;
    const nm = p?.display_name || p?.name || p?.common_name || [p?.firstname, p?.lastname].filter(Boolean).join(' ');
    if (nm) map[p.id] = nm;
  };
  (Array.isArray(data?.squad) ? data.squad : []).forEach((s) => add(s?.player || s));
  (Array.isArray(data?.overview?.players) ? data.overview.players : []).forEach((p) => add(p));
  return map;
}

function topPlayerList(arr, nameMap, field) {
  if (!Array.isArray(arr) || !arr.length) return '—';
  return arr.slice(0, 3).map((e) => {
    const nm = nameMap?.[e?.player_id] || `#${e?.player_id ?? '?'}`;
    const v = e?.[field] ?? e?.total ?? '';
    return `${nm}${v !== '' ? ` (${v})` : ''}`;
  }).join(' · ');
}

export function formatStatValue(code, v, nameMap) {
  if (v == null || v === '') return '—';
  if (typeof v !== 'object') return String(v);

  if (/highest.?rated/i.test(code)) {
    const pid = v.player_id ?? v.id;
    const r = v.rating ?? v.average_rating ?? v.rating_avg;
    const nm = nameMap?.[pid] || (pid != null ? `Player #${pid}` : '');
    return [nm, r != null ? Number(r).toFixed(2) : null].filter(Boolean).join(' ');
  }
  if (Array.isArray(v.most_appearing_players)) return topPlayerList(v.most_appearing_players, nameMap, 'matches');
  if (Array.isArray(v.most_substituted_players)) return topPlayerList(v.most_substituted_players, nameMap, 'total');
  if (Array.isArray(v.most_injured_players)) return topPlayerList(v.most_injured_players, nameMap, 'total');

  if (code === 'players-footing') return `Right ${v.right ?? 0} · Left ${v.left ?? 0} · Both/Unknown ${v.unknown ?? 0}`;
  if (code === 'average-player-height') {
    return ['avg_defender_height', 'avg_midfielder_height', 'avg_goalkeeper_height', 'avg_attacker_height']
      .filter((k) => v[k] != null)
      .map((k) => `${k.replace('avg_', '').replace('_height', '').slice(0, 3).toUpperCase()} ${v[k]}`)
      .join(' · ') || '—';
  }
  if (code === 'ball-possession') return `${v.average ?? v.percentage ?? v.count}%`;
  if (code === 'shots') return `${v.total ?? v.count ?? '—'} (on target ${v.on_target ?? '—'})`;
  if (code === 'average-points-per-game') return String(v.average_points_per_game ?? '—');
  if (code === 'shot-conversion-rate') return `${v.conversion_rate_pct ?? '—'}%`;
  if (code === 'scoring-minutes' || code === 'conceded-scoring-minutes') {
    const buckets = Object.keys(v).filter((k) => /-/.test(k) && typeof v[k] === 'object');
    if (!buckets.length) return '—';
    return buckets.map((b) => `${b}: ${v[b].count ?? '—'}`).join(' · ');
  }
  if (code === 'number-of-goals') {
    const overs = Object.keys(v).filter((k) => /^over_/.test(k));
    if (!overs.length) return '—';
    return overs.slice(0, 4).map((k) => `${k.replace('over_', '').replace('_', '.')}+: ${v[k]?.matches?.percentage ?? v[k]?.matches?.count ?? '—'}%`).join(' · ');
  }
  if ('scored' in v && 'missed' in v) {
    return `Scored ${v.scored} · Missed ${v.missed}${v.conversion_rate != null ? ` (${v.conversion_rate}%)` : ''}`;
  }

  if ('fouls_per_card' in v && 'cards_per_foul' in v) {
    return `${v.fouls_per_card} fouls/card · ${v.cards_per_foul} cards/foul`;
  }
  if ('most_scored_half' in v) {
    return `${v.most_scored_half} (${v.most_scored_half_goals ?? '—'} goals)`;
  }
  if ('most_frequent_scoring_minute' in v) {
    return `${v.most_frequent_scoring_minute}′ · ${v.amount_of_goals ?? '—'} goals`;
  }
  if ('total_interceptions' in v && 'interceptions_per_game' in v) {
    return `${v.total_interceptions} total (${v.interceptions_per_game}/game)`;
  }
  if ('passes_per_game' in v) {
    return [v.total_passes != null ? `${v.total_passes} total` : null, `${v.passes_per_game}/game`, v.passes_per_goal != null ? `${v.passes_per_goal}/goal` : null].filter(Boolean).join(' · ');
  }
  if ('assists_per_game' in v) {
    return [v.total_assists != null ? `${v.total_assists} total` : null, `${v.assists_per_game}/game`, v.minutes_per_assist != null ? `${v.minutes_per_assist} min/assist` : null].filter(Boolean).join(' · ');
  }
  if ('won_both_halves' in v && 'scored_both_halves' in v) {
    return `Won both ${v.won_both_halves} · Scored both ${v.scored_both_halves} · Comebacks ${v.comebacks ?? '—'}`;
  }
  if ('national_team_players' in v && Array.isArray(v.national_team_players)) {
    const arr = v.national_team_players;
    return `${arr.length} player(s) · ${arr.slice(0, 4).map((e) => e?.team || '—').join(', ')}`;
  }
  if ('gf_over_0_5' in v) {
    return [['0.5+', v.gf_over_0_5], ['1.5+', v.gf_over_1_5], ['2.5+', v.gf_over_2_5], ['3.5+', v.gf_over_3_5]].map(([k, val]) => `${k}: ${val ?? '—'}`).join(' · ');
  }
  if (Object.keys(v).length === 1 && 'scoring_frequency' in v) return `Every ${v.scoring_frequency} min`;
  if (Object.keys(v).length === 1 && 'total_minutes_played' in v) return String(v.total_minutes_played);

  if (v.all && typeof v.all === 'object' && 'count' in v.all) {
    return `${v.all.count}${v.all.percentage != null ? ` (${v.all.percentage}%)` : ''}`;
  }
  if ('count' in v) return String(v.count);
  if ('total' in v) return String(v.total);
  if ('average' in v) return String(v.average);
  if ('value' in v) return String(v.value);

  const allScalar = Object.keys(v).length && Object.values(v).every((x) => x == null || typeof x !== 'object');
  if (allScalar) {
    const entries = Object.entries(v).filter(([, val]) => val != null);
    if (entries.length === 1) return String(entries[0][1]);
    return entries.slice(0, 4).map(([k, val]) => `${titleize(k)}: ${val}`).join(' · ');
  }
  return JSON.stringify(v).slice(0, 60);
}
